import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { KeycloakService } from '@inoventoryapps/keycloak-angular';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { KeyService } from '../_services/_utils/key.service';
import { SERVICE_URL } from '../_urls/service.urls';

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {
    private readonly excludedRoutes = new Set([
        SERVICE_URL.REPORTS,
        'lokos-transaction/master/organization/federation',
        'lokos-transaction/clf/memberLoanDetailsReceipt',
        'clf-profile/basic/',
        'clf-profile/clf-operation',
        'clf-profile/clf-staff',
        'clf-profile/cmtc',
        'clf-profile/compliance',
        'clf-profile/srp',
        'clf-profile/clf-location',
        'api/mclf',
        'governance',
        'createOrUpdateModelclf',
        'clf-activity/main/data',
        'notification/sendEmail',
    ]);
    isDebug = false;

    constructor(
        private keyService: KeyService,
        private keyclockService: KeycloakService,
        private activatedRoute: ActivatedRoute
    ) {}

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        const env = environment as {
            authMode?: string;
            profilesApiBaseUrl?: string;
        };
        if (
            env.authMode === 'mock' ||
            req.url.includes('/assets/') ||
            req.url.split('?')[0].endsWith('.json') ||
            req.url.includes('lokos-masterdata') ||
            (env.profilesApiBaseUrl &&
                req.url.startsWith(env.profilesApiBaseUrl))
        ) {
            return next.handle(req);
        }

        this.isDebug = this.activatedRoute.snapshot.queryParams['debug'] === 'true';

        return new Observable((observer) => {
            this.keyService.getClientKey().then(async () => {
                try {
                    const url = req.url.includes(SERVICE_URL.KEYCLOCK_SERVICE)
                        ? environment.keyClockApiBaseUrl
                        : environment.apiBaseUrl;
                    const serverPublicKey =
                        await this.keyService.getServerPublicKey(
                            `${url}${SERVICE_URL.CRYPTO_JWKS}`,
                            req.url
                        );
                    let encryptedBody: any;
                    if (this.isByPass(req) && !this.isDebug) {
                        await (this.keyclockService as any)
                            .encrypt(
                                { publicKey: serverPublicKey },
                                req.body
                            )
                            .then((encryptedData: any) => {
                                encryptedBody = encryptedData;
                            });
                    } else {
                        encryptedBody = req.body;
                    }

                    let setHeaders: any = {
                        'x-encrypted':
                            this.isByPass(req) && !this.isDebug
                                ? 'true'
                                : 'false',
                    };
                    if (!req.url.includes('signer')) {
                        const dpopUrl = req.url.split('?')[0].split('#')[0];
                        setHeaders = {
                            ...setHeaders,
                            DPoP: await (this.keyclockService as any).dpopProof(
                                dpopUrl,
                                req.method,
                                encryptedBody?.iv || '',
                                this.keyService.getKid(dpopUrl)
                            ),
                        };
                    }

                    const newReq = req.clone({
                        body:
                            this.isByPass(req) && !this.isDebug
                                ? { data: encryptedBody.encrypted }
                                : req.body,
                        setHeaders: { ...setHeaders },
                    });

                    next.handle(newReq)
                        .pipe(
                            switchMap(async (event: HttpEvent<any>) => {
                                if (
                                    event instanceof HttpResponse &&
                                    event.headers.get('x-encrypted') === 'true'
                                ) {
                                    if (event.body && event.body['data']) {
                                        const decodedBody = await (
                                            this.keyclockService as any
                                        ).decrypt(
                                            { publicKey: serverPublicKey },
                                            event.body['data'],
                                            event.headers.get(
                                                'x-crypto-enabled'
                                            )
                                        );
                                        observer.next(
                                            event.clone({ body: decodedBody })
                                        );
                                    } else {
                                        observer.next(
                                            event.clone({ body: {} })
                                        );
                                    }
                                } else if (event instanceof HttpResponse) {
                                    observer.next(event);
                                } else {
                                    observer.next(event);
                                }
                            }),
                            catchError((error) => {
                                observer.error(error);
                                return [];
                            })
                        )
                        .subscribe({
                            complete: () => observer.complete(),
                        });
                } catch (error) {
                    observer.error(error);
                }
            });
        });
    }

    isByPass(req: HttpRequest<any>): boolean {
        if (!environment.enableEncryption) {
            return false;
        }
        return !Array.from(this.excludedRoutes).some((route) =>
            req.url.includes(route)
        );
    }
}
